import { useEffect, useState } from "react";
import { Loader2, Paperclip, Send, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { estApi } from "../lib/api";
import IncompleteLinesDialog from "./IncompleteLinesDialog";

export default function EmailProposalDialog({ open, onOpenChange, estimateId, onSent,
                                              restrictEmails, restrictLabel, companyId = "" }) {
  const [draft, setDraft] = useState(null);
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [newTo, setNewTo] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(null);
    estApi.getEmailDraft(estimateId, companyId).then((d) => {
      setDraft(d);
      // One GC at a time: only that customer's contacts are addressed, so a multi-GC bid goes out
      // as one proposal email per customer instead of one email that names them all.
      const only = (restrictEmails || []).filter(Boolean);
      setTo(only.length ? only : (d.to || []));
      setCc(only.length ? [] : (d.cc || []));
      setSubject(d.subject || "");
      setBody(d.body || "");
    }).catch(() => toast.error("Could not build the email draft"));
  }, [open, estimateId, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [blocked, setBlocked] = useState(null);

  const send = async () => {
    setConfirm(false);
    setSending(true);
    try {
      const res = await estApi.sendProposal(estimateId, { to, cc, subject, body, company_id: companyId });
      toast.success(`Proposal sent to ${res.sent_to.length} recipient(s)`);
      onSent?.(res);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "incomplete_lines") setBlocked(detail);
      else toast.error(typeof detail === "string" ? detail : "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <IncompleteLinesDialog
        open={!!blocked} onOpenChange={(v) => !v && setBlocked(null)}
        estimateId={estimateId} detail={blocked} onResolved={send}
      />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" data-testid="email-proposal-dialog">
          <DialogHeader><DialogTitle>
            Email proposal{restrictLabel ? ` — ${restrictLabel}` : ""}
          </DialogTitle></DialogHeader>

          {!draft ? (
            <div className="flex items-center justify-center py-10 text-xs text-slate-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing draft…
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-slate-500">To</label>
                <div className="flex flex-wrap items-center gap-1.5 rounded border border-slate-200 p-1.5"
                     data-testid="email-to-chips">
                  {to.map((e) => (
                    <span key={e} className="flex items-center gap-1 rounded-full bg-[#007BFF]/10 px-2 py-0.5 text-[11px] text-[#007BFF]">
                      {e}
                      <button onClick={() => setTo(to.filter((x) => x !== e))}
                              data-testid={`email-to-remove-${e}`}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input value={newTo} onChange={(ev) => setNewTo(ev.target.value)}
                         onKeyDown={(ev) => {
                           if (ev.key === "Enter" && newTo.trim()) {
                             setTo([...new Set([...to, newTo.trim()])]);
                             setNewTo("");
                           }
                         }}
                         placeholder="add email + Enter"
                         className="min-w-[150px] flex-1 text-[11px] outline-none"
                         data-testid="email-to-input" />
                </div>
                {(draft.available_to || []).filter((c) => !to.includes(c.email)).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(draft.available_to || []).filter((c) => !to.includes(c.email)).map((c) => (
                      <button key={c.email} onClick={() => setTo([...to, c.email])}
                              className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 hover:bg-slate-200"
                              data-testid={`email-add-contact-${c.email}`}>
                        + {c.name || c.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="text-[11px] text-slate-500">CC</label>
                <div className="flex flex-wrap gap-1.5" data-testid="email-cc-list">
                  {(draft.cc || []).length === 0 && (
                    <span className="text-[11px] text-slate-400">
                      No AM or estimator email on this estimate.
                    </span>
                  )}
                  {(draft.cc || []).map((e) => (
                    <button key={e} onClick={() => setCc(cc.includes(e)
                      ? cc.filter((x) => x !== e) : [...cc, e])}
                            className={`rounded-full px-2 py-0.5 text-[11px] ${
                              cc.includes(e) ? "bg-[#007BFF]/10 text-[#007BFF]"
                                : "bg-slate-100 text-slate-500"}`}
                            data-testid={`email-cc-${e}`}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[11px] text-slate-500">Subject</label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                       className="h-8 text-xs" data-testid="email-subject" />
              </div>

              <div>
                <label className="text-[11px] text-slate-500">Body</label>
                <textarea value={body} rows={11} onChange={(e) => setBody(e.target.value)}
                          className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs leading-relaxed focus:border-[#007BFF] focus:outline-none"
                          data-testid="email-body" />
              </div>

              <div className="flex items-center gap-1.5 rounded bg-slate-50 px-2 py-1.5 text-[11px] text-slate-600"
                   data-testid="email-attachment">
                <Paperclip className="h-3 w-3" /> {draft.attachment_name} — regenerated automatically
                if the estimate changed since the last version
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}
                    data-testid="email-cancel">Cancel</Button>
            <Button variant="data" onClick={() => setConfirm(true)} disabled={!draft || to.length === 0 || sending}
                    data-testid="email-send-btn">
              {sending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                       : <Send className="mr-2 h-3.5 w-3.5" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="email-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send to {to.length} recipient(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              {to.join(", ")}{cc.length ? ` · cc ${cc.join(", ")}` : ""}. The current proposal PDF
              is attached and the estimate is marked as Bid Submitted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="email-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={send} data-testid="email-confirm-send">
              Yes, send it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
